/**
 * Floating Lupa chat that follows the user through every Hub and item (07-hubs §5;
 * PLC Home's GlobalPetalWidget pattern — Petal's widget was forked from Lupa's).
 *
 * Mounted once at the app root (routes/AppRoutes.tsx → GlobalLupaWidget), so it
 * stays open and keeps its conversation while the user moves between Hubs. It
 * reads and writes the same store as the full Lupa page (LupaChatContext), so
 * "Open in Lupa" shows the same conversation full-page.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faPaperPlane,
  faPenToSquare,
  faListUl,
  faUpRightAndDownLeftFromCenter,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import BrandIcon from '../assets/DWA_WolfHead_Favicon_Web.png';
import { useLupaChat } from '../contexts/LupaChatContext';
import { DEFAULT_CONVERSATION_TITLE } from '../contexts/lupaChatHelpers';
import { DWA } from '../theme/dwaTokens';
import { OPEN_LUPA_EVENT } from '../hubs/hubNav';

/** Strip citation markers (the full page renders them as links) and render **bold**. */
const clean = (s: string) => s.replace(/\[\[ref:[^\]]+\]\]/g, '').trim();
const withBold = (text: string, keyBase: string) =>
  text.split('**').map((seg, j) => (j % 2 === 1 ? <strong key={`${keyBase}-${j}`}>{seg}</strong> : seg));

export const LupaWidget: React.FC = () => {
  const navigate = useNavigate();
  const {
    conversations,
    currentConversation,
    isLoading,
    sendMessage,
    selectConversation,
    startNewConversation,
  } = useLupaChat();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'chat' | 'list'>('chat');
  const [input, setInput] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const messages = currentConversation?.messages ?? [];

  // The Hubs top bar's "Ask Lupa" button opens the widget; Home's tips also pre-fill
  // the message box (never sent without the person pressing send).
  useEffect(() => {
    const onOpen = (e: Event) => {
      const prompt = (e as CustomEvent<{ prompt?: string } | null>).detail?.prompt;
      if (prompt) setInput(prompt);
      setView('chat');
      setOpen(true);
    };
    window.addEventListener(OPEN_LUPA_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_LUPA_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, messages.length, isLoading]);

  const send = async () => {
    const content = input.trim();
    if (!content || isLoading) return;
    setInput('');
    // TODO(07-hubs §5, page context): send the current Hub / item / client filter
    // with each widget message ("user is on Web → Backup Monitor"). The backend
    // /chat endpoint (services/agent/app/main.py `chat`) doesn't accept a
    // page-context field yet, so nothing is sent. When it does, derive it with
    // resolveHubLocation(pathname, search) from hubs/hubs.ts. Context only — what
    // Lupa may read stays decided server-side by the user's access.
    await sendMessage(content);
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} aria-label="Open Lupa chat" style={launcher}>
        <img src={BrandIcon} alt="" style={{ width: 34, height: 34, objectFit: 'cover' }} />
      </button>
    );
  }

  const active = conversations.filter((c) => !c.isArchived);

  return (
    <div style={panel} role="dialog" aria-label="Lupa chat">
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          {view === 'list' ? (
            <button type="button" onClick={() => setView('chat')} style={iconBtn} aria-label="Back to chat" title="Back to chat">
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          ) : (
            <img src={BrandIcon} alt="" style={{ width: 24, height: 24, objectFit: 'cover' }} />
          )}
          <span style={{ fontWeight: 700, color: DWA.onDark, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {view === 'list' ? 'Your conversations' : 'Lupa'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.1rem', flexShrink: 0 }}>
          <button type="button" onClick={() => setView(view === 'list' ? 'chat' : 'list')} style={iconBtn} aria-label="Conversations" title="Conversations">
            <FontAwesomeIcon icon={faListUl} />
          </button>
          <button
            type="button"
            onClick={() => {
              startNewConversation();
              setView('chat');
            }}
            style={iconBtn}
            aria-label="New chat"
            title="New chat"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate('/lupa');
            }}
            style={iconBtn}
            aria-label="Open in Lupa"
            title="Open in Lupa"
          >
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
          </button>
          <button type="button" onClick={() => setOpen(false)} style={iconBtn} aria-label="Close Lupa chat" title="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      {view === 'list' ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
          {active.length === 0 ? (
            <p style={{ color: DWA.textMuted, fontSize: '0.85rem', textAlign: 'center', marginTop: '2rem' }}>No conversations yet.</p>
          ) : (
            active.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  selectConversation(c.id);
                  setView('chat');
                }}
                style={{
                  ...convoRow,
                  background: c.id === currentConversation?.id ? DWA.goldSoft : 'transparent',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: DWA.ink, display: 'block' }}>
                  {c.title || DEFAULT_CONVERSATION_TITLE}
                </span>
                <span style={{ fontSize: '0.75rem', color: DWA.textMuted }}>
                  {c.messages.length} message{c.messages.length === 1 ? '' : 's'}
                </span>
              </button>
            ))
          )}
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {messages.length === 0 && !isLoading && (
            <div style={{ textAlign: 'center', color: DWA.textSecondary, fontSize: '0.85rem', marginTop: '2rem', padding: '0 1rem' }}>
              <p style={{ margin: 0, lineHeight: 1.5 }}>Hi, I'm Lupa. Ask me anything, from any Hub.</p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={m.role === 'user' ? userBubble : botBubble}>{withBold(clean(m.content), m.id)}</div>
            </div>
          ))}
          {isLoading && <div style={{ ...botBubble, color: DWA.textMuted }}>Lupa is thinking…</div>}
          <div ref={endRef} />
        </div>
      )}

      {view === 'chat' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem', borderTop: `1px solid ${DWA.cardBorder}` }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Message Lupa…"
            aria-label="Message Lupa"
            style={composerInput}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            style={{ ...sendBtn, opacity: input.trim() && !isLoading ? 1 : 0.5 }}
            aria-label="Send"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </form>
      )}
    </div>
  );
};

// ---- styles ----
const launcher: React.CSSProperties = {
  position: 'fixed',
  bottom: '1.25rem',
  right: '1.25rem',
  zIndex: 1000,
  width: 58,
  height: 58,
  borderRadius: '50%',
  border: `2px solid ${DWA.gold}`,
  cursor: 'pointer',
  background: `linear-gradient(135deg, ${DWA.dark}, ${DWA.darkSoft})`,
  boxShadow: '0 8px 24px -6px rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const panel: React.CSSProperties = {
  position: 'fixed',
  bottom: '1rem',
  right: '1rem',
  zIndex: 1000,
  width: 'min(380px, calc(100vw - 2rem))',
  height: 'min(560px, calc(100dvh - 2rem))',
  background: '#ffffff',
  borderRadius: DWA.radius,
  border: `1px solid ${DWA.cardBorder}`,
  boxShadow: '0 20px 50px -12px rgba(0,0,0,0.45)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  fontFamily: DWA.font,
};
const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.5rem',
  padding: '0.6rem 0.75rem',
  background: DWA.dark,
};
const iconBtn: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: DWA.radius,
  border: 'none',
  background: 'transparent',
  color: DWA.onDark,
  cursor: 'pointer',
  fontSize: '0.85rem',
};
const userBubble: React.CSSProperties = {
  background: DWA.gold,
  color: DWA.onGold,
  padding: '0.55rem 0.8rem',
  borderRadius: '0.9rem 0.9rem 0.2rem 0.9rem',
  fontSize: '0.88rem',
  lineHeight: 1.45,
  maxWidth: '85%',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
const botBubble: React.CSSProperties = {
  background: 'rgba(0,0,0,0.05)',
  color: DWA.ink,
  padding: '0.55rem 0.8rem',
  borderRadius: '0.9rem 0.9rem 0.9rem 0.2rem',
  fontSize: '0.88rem',
  lineHeight: 1.5,
  maxWidth: '85%',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
const convoRow: React.CSSProperties = {
  width: '100%',
  textAlign: 'left',
  padding: '0.6rem 0.7rem',
  borderRadius: DWA.radius,
  border: 'none',
  cursor: 'pointer',
  marginBottom: '0.15rem',
  fontFamily: 'inherit',
};
const composerInput: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: '0.6rem 0.8rem',
  borderRadius: DWA.radius,
  border: '1px solid rgba(0,0,0,0.15)',
  fontSize: '16px',
  fontFamily: 'inherit',
  color: DWA.ink,
  outline: 'none',
};
const sendBtn: React.CSSProperties = {
  width: 42,
  borderRadius: DWA.radius,
  border: 'none',
  background: DWA.gold,
  color: DWA.onGold,
  cursor: 'pointer',
  fontSize: '0.85rem',
};
